import axios from 'axios';
import to from '../utils/to';

/* eslint-disable no-undef */

const BASE_URL = process.env.REACT_APP_BASE_URL;
console.log("Compiled BASE_URL:", BASE_URL);
const CLOUDINARY_UPLOAD_URL = process.env.REACT_APP_IMAGE_UPLOAD;
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;
const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD;

const getBearerToken = () => {
  const accessToken = localStorage.getItem('accessToken');
  return accessToken ? `Bearer ${accessToken}` : null;
};

const getRefreshToken = () => localStorage.getItem('refreshToken');

const getRequestHeaders = (noAuthHeader) => {
  if (!noAuthHeader) {
    const bearerToken = getBearerToken();
    return bearerToken ? { Authorization: bearerToken } : {};
  }
  return {};
};

const getFullUrl = (url) => `${BASE_URL}/${url}`;

const successHandler = (res) => res.data;

const errorHandler = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  const message =
    data?.message ||
    data?.error ||
    data?.detail ||
    err?.message ||
    'Request failed';

  throw {
    status,
    message,
    data,
    url: err?.config?.url,
    method: err?.config?.method,
  };
};

const nonRetryGET = async (url, options, { noAuthHeader } = {}) => {
  return await axios
    .get(getFullUrl(url), { headers: getRequestHeaders(noAuthHeader), ...options })
    .then(successHandler)
    .catch(errorHandler);
};

const nonRetryDELETE = async (url, body, options, { noAuthHeader } = {}) => {
  return await axios
    .delete(getFullUrl(url), {
      headers: getRequestHeaders(noAuthHeader),
      data: body,
      ...options,
    })
    .then(successHandler)
    .catch(errorHandler);
};

const nonRetryPOST = (url, body, options, { noAuthHeader } = {}) => {
  return axios
    .post(getFullUrl(url), body, { headers: getRequestHeaders(noAuthHeader), ...options })
    .then(successHandler)
    .catch(errorHandler);
};

const nonRetryPUT = (url, body, options, { noAuthHeader } = {}) => {
  return axios
    .put(getFullUrl(url), body, { headers: getRequestHeaders(noAuthHeader), ...options })
    .then(successHandler)
    .catch(errorHandler);
};

const withAuthRetry = async (method, ...params) => {
  const [err, result] = await to(method(...params));
  if (err) {
    if (err.status === 401) {
      const [refreshError, data] = await to(
        nonRetryPOST('v1/token/refresh', { refreshToken: getRefreshToken() }, {}, { noAuthHeader: true }),
      );
      if (refreshError) {
        throw err;
      }
      const refreshedAccessToken = data?.accessToken || data?.data?.accessToken;
      if (!refreshedAccessToken) {
        throw err;
      }
      localStorage.setItem('accessToken', refreshedAccessToken);
      const [err2, result2] = await to(method(...params));
      if (err2) {
        throw err2;
      }
      return result2;
    }
    throw err;
  }
  return result;
};

const getCloudinaryUploadUrl = () => {
  if (CLOUDINARY_UPLOAD_URL) {
    return CLOUDINARY_UPLOAD_URL;
  }

  if (CLOUDINARY_CLOUD) {
    return `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`;
  }

  throw {
    status: 500,
    message:
      'Image upload configuration is missing. Please set REACT_APP_IMAGE_UPLOAD or REACT_APP_CLOUDINARY_CLOUD.',
    data: {
      CLOUDINARY_UPLOAD_URL,
      CLOUDINARY_CLOUD,
      CLOUDINARY_PRESET,
    },
    url: null,
    method: 'post',
  };
};

const POST_IMAGE = async (image) => {
  const uploadUrl = getCloudinaryUploadUrl();
  const cloudNameFromUrl = uploadUrl?.match(/\/v1_1\/([^/]+)\/image\/upload/)?.[1] || null;

  if (!image) {
    throw {
      status: 400,
      message: 'No image file was provided',
      data: null,
      url: uploadUrl,
      method: 'post',
    };
  }

  if (!CLOUDINARY_PRESET) {
    throw {
      status: 500,
      message: 'Missing REACT_APP_CLOUDINARY_PRESET environment variable',
      data: null,
      url: uploadUrl,
      method: 'post',
    };
  }

  const formData = new FormData();
  formData.append('file', image);
  formData.append('upload_preset', CLOUDINARY_PRESET);

  try {
    const res = await axios.post(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const cloudinaryResponse = res.data;
    console.log('🖼️ Cloudinary Upload Response:', cloudinaryResponse);

    // Cloudinary should return secure_url; if not, build a usable URL from public_id.
    let imageUrl = cloudinaryResponse?.secure_url || cloudinaryResponse?.url;

    if (!imageUrl && cloudinaryResponse?.public_id && (CLOUDINARY_CLOUD || cloudNameFromUrl)) {
      const cloudName = CLOUDINARY_CLOUD || cloudNameFromUrl;
      const version = cloudinaryResponse?.version || '';
      imageUrl = `https://res.cloudinary.com/${cloudName}/image/upload/v${version}/${cloudinaryResponse.public_id}`;
      console.log('📝 Constructed Image URL:', imageUrl);
    }

    if (!imageUrl) {
      throw {
        status: 502,
        message: 'No image URL returned from Cloudinary',
        data: cloudinaryResponse,
        url: uploadUrl,
        method: 'post',
      };
    }

    return { ...cloudinaryResponse, secure_url: imageUrl, url: imageUrl };
  } catch (err) {
    console.error('❌ Cloudinary Upload Error:', {
      status: err?.response?.status || err?.status,
      message: err?.message,
      data: err?.response?.data || err?.data,
      url: uploadUrl,
    });
    return errorHandler(err);
  }
};

const GET = (...params) => withAuthRetry(nonRetryGET, ...params);
const POST = (...params) => withAuthRetry(nonRetryPOST, ...params);
const PUT = (...params) => withAuthRetry(nonRetryPUT, ...params);
const DELETE = (...params) => withAuthRetry(nonRetryDELETE, ...params);

const DOWNLOAD = (url) =>
  axios({
    url: getFullUrl(url),
    method: 'GET',
    responseType: 'blob',
    headers: getRequestHeaders(false),
  });

export { GET, POST, PUT, nonRetryGET, nonRetryPOST, POST_IMAGE, DOWNLOAD, DELETE };